// Shared cozy palette for the Chockytown world and UI.
// Values stay as numeric hexes for cheap Three.js material construction; use
// css() where canvas/CSS needs a #rrggbb string. Keep this file allocation-free
// in hot paths by exporting constants, not mutable Color objects.
export const PALETTE = Object.freeze({
  skyCream: 0xf4f0e8,
  skyMorning: 0xf4e3cf,
  skyPeach: 0xf2c9a0,
  skyRose: 0xb98a98,
  nightIndigo: 0x1a2442,
  nightBlue: 0x34476f,
  ink: 0x1b1f2a,
  muted: 0x6b6872,
  border: 0xddd6c9,

  coral: 0xe8776a,
  coralSoft: 0xee9a8e,
  gold: 0xe8c96a,
  goldSoft: 0xf4df9a,
  skyBlue: 0x8fb8d8,
  skyBlueSoft: 0xb9d4e6,
  lavender: 0xa995cf,
  lavenderSoft: 0xd3c7ec,
  sage: 0x86a96f,
  sageSoft: 0xb8c99a,
  cream: 0xf3ecd8,
  panel: 0xfffcf4,

  meadowBase: 0x78a85a,
  meadowDark: 0x5f8a4f,
  meadowLight: 0x9dbc78,
  leafBase: 0x6f9b55,
  leafDark: 0x557d45,
  leafTip: 0x99bb73,

  grassBase: 0x75a249,
  grassHighlightA: 0x96b65c,
  grassHighlightB: 0x8aaa52,
  grassShadeA: 0x4e7032,
  grassShadeB: 0x5c7c3a,
  grassDry: 0xb4b060,
  grassBladeDark: 0x285a1e,
  grassBladeLight: 0x64a05a,
  grassMacroWarm: 0xaaa578,
  grassMacroCool: 0x3c4637,

  townDirtLight: 0xd6be96,
  townDirtMid: 0xc8a878,
  townDirtDark: 0x785c38,
  townDirtRim: 0xb08c5c,
  pebbleDark: 0x6e604a,
  pebbleLight: 0xb4a890,

  wood: 0x7f5d3f,
  woodDark: 0x5f3e2a,
  woodLight: 0xb88b5f,
  roofTerracotta: 0x9e563c,
  roofBrown: 0x7c5132,
  doorDark: 0x3d2a1b,

  stone: 0xa99d8b,
  stoneLight: 0xc7b89c,
  stoneDark: 0x8f826f,
  pathWarm: 0xbcaa86,
  sand: 0xdfc89e,
  water: 0x77b8cf,

  windowDay: 0xb9d4e6,
  windowNight: 0xffd86f,
  lanternCore: 0xffcf8a,
  lanternEmissive: 0xffb14a,
  lanternHalo: 0xffc066,
  lanternLight: 0xffb86b,
  contactShadow: 0x2c231b,

  wordRed: 0x7b3436,
  wordRedDark: 0x623033,
  spookyEye: 0xffc24a,
});

export const ACCENTS = Object.freeze([
  PALETTE.coral,
  PALETTE.gold,
  PALETTE.skyBlue,
  PALETTE.lavender,
  PALETTE.sage,
  PALETTE.cream,
]);

export const BUILDING_SCHEMES = Object.freeze({
  popSafari:      { wall: PALETTE.cream,       roof: PALETTE.roofTerracotta },
  numberZoo:      { wall: PALETTE.sand,        roof: PALETTE.roofBrown },
  adventureBlue:  { wall: PALETTE.skyBlueSoft, roof: PALETTE.skyBlue },
  wordCream:      { wall: PALETTE.sand,        roof: PALETTE.woodDark },
  mathChef:       { wall: PALETTE.cream,       roof: PALETTE.wordRed },
  royalFeast:     { wall: PALETTE.goldSoft,    roof: PALETTE.leafDark },
  groveCream:     { wall: PALETTE.cream,       roof: PALETTE.roofBrown },
  groveSage:      { wall: PALETTE.sageSoft,    roof: PALETTE.leafDark },
  groveSky:       { wall: PALETTE.skyBlueSoft, roof: PALETTE.skyBlue },
  wordLavender:   { wall: 0xe8dcc0,            roof: 0x7c5a9c },
  wordGreen:      { wall: 0xd8e4d0,            roof: 0x3f7048 },
  wordTerracotta: { wall: 0xf0e2c8,            roof: 0xa84e2c },
});

export const ZONE_VISUALS = Object.freeze({
  village:  { sky: 0xffffff, fog: 0xffffff, warm: 0.00, skyMix: 0.00, fogMix: 0.00 },
  wordville:{ sky: 0xfff8dc, fog: 0xfff0bd, warm: 0.10, skyMix: 0.16, fogMix: 0.22 },
  grove:    { sky: 0xe7fbef, fog: 0xd8f3dd, warm:-0.04, skyMix: 0.16, fogMix: 0.22 },
  obby:     { sky: 0xf0ecff, fog: 0xe4e1ff, warm: 0.02, skyMix: 0.16, fogMix: 0.22 },
  green:    { sky: 0xfff7df, fog: 0xfff1c6, warm: 0.06, skyMix: 0.16, fogMix: 0.22 },
  numberton:{ sky: 0xe9f2ff, fog: 0xdcebff, warm: 0.00, skyMix: 0.16, fogMix: 0.22 },
});

export function css(hex) {
  return `#${hex.toString(16).padStart(6, '0')}`;
}

export function rgb(hex) {
  return `${(hex >> 16) & 255},${(hex >> 8) & 255},${hex & 255}`;
}
